// Pure, framework-agnostic helpers for the Stoplight / Dashboard Item feature.
// Single source of truth on the frontend for stoplight rules, eligibility,
// operator metadata. No React, no DOM.

export const COLORS = ["red", "yellow", "green"];

export const MAX_DASHBOARD_VARIABLES = 5;

export const MIN_OPERATORS = [
  { value: ">=", label: "Greater than or equal to" },
  { value: ">", label: "Greater than" },
];

export const MAX_OPERATORS = [
  { value: "<=", label: "Less than or equal to" },
  { value: "<", label: "Less than" },
];

export const compare = (value, operator, target) => {
  if (
    value === null ||
    value === undefined ||
    target === null ||
    target === undefined ||
    target === ""
  ) {
    return false;
  }
  const numericValue = Number(value);
  const numericTarget = Number(target);
  if (Number.isNaN(numericValue) || Number.isNaN(numericTarget)) {
    return false;
  }
  switch (operator) {
    case ">":
      return numericValue > numericTarget;
    case ">=":
      return numericValue >= numericTarget;
    case "<":
      return numericValue < numericTarget;
    case "<=":
      return numericValue <= numericTarget;
    default:
      return false;
  }
};

export const getMatchedColors = (value, range) => {
  if (!range) {
    return [];
  }
  return COLORS.filter((color) => {
    const minMatch = compare(
      value,
      range[`${color}MinOp`],
      range[`${color}MinVal`]
    );
    const maxMatch = compare(
      value,
      range[`${color}MaxOp`],
      range[`${color}MaxVal`]
    );
    return minMatch && maxMatch;
  });
};

// Whether a variable is *allowed* to be flagged as a Dashboard Item
// (used by the configurator to gate the toggle). Does NOT consider
// the user's chosen `isDashboardItem` flag.
export const isDashboardEligibleVariable = (variable) => {
  return (
    variable?.type?.toLowerCase() === "number" &&
    !variable?.isAccreditationType &&
    variable?.name?.trim().toLowerCase() !== "mcee result"
  );
};

// Variables that should actually appear on the runtime dashboard:
// eligible AND flagged AND sorted by id AND capped at MAX_DASHBOARD_VARIABLES.
export const selectDashboardVariables = (vars) => {
  return (vars || [])
    .filter(
      (variable) =>
        isDashboardEligibleVariable(variable) &&
        Boolean(variable?.isDashboardItem)
    )
    .sort((a, b) => Number(a.id || 0) - Number(b.id || 0))
    .slice(0, MAX_DASHBOARD_VARIABLES);
};

// Value used for stoplight range checks: sum-mode uses live currentValue;
// "use current value" mode (variableContext false) uses starting value only.
const stoplightSourceValue = (variable) => {
  if (variable?.variableContext) {
    return variable?.currentValue ?? variable?.value ?? variable?.startVal;
  }
  return variable?.currentValue ?? variable?.startVal ?? variable?.value;
};

// Map selected dashboard variables into render-ready rows
// of the shape { id, label, colors }.
export const buildDashboardRows = (vars, range) => {
  return selectDashboardVariables(vars).map((variable) => ({
    id: variable.id,
    label: variable.name,
    colors: getMatchedColors(stoplightSourceValue(variable), range),
  }));
};
